import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import TickerInput from '../../../components/TickerInput';
import { Heading, Text } from '../../../components/Typography';
import DocumentList from './components/DocumentList';
import SectionSelector from './components/SectionSelector';
import ContextWindowStats from './components/ContextWindowStats';
import PromptInput from './components/PromptInput';
import SummaryResult from './components/SummaryResult';
import { AppDispatch, RootState } from '../../../store';
import { setCompanyAsync, reset } from '../../../store/summaryTest/slice';
import styles from './SummaryTestPage.module.scss';
import SummaryTypeSelector from './components/SummaryTypeSelector';
import PromptNameSelector from './components/PromptNameSelector';
import { Company } from '../../../store/company/types';

const SummaryTestPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { companyId, selectedDocuments, prompt, summaryType, promptName } = useSelector(
    (state: RootState) => state.summaryTest,
  );
  const selectedDocumentIds = Object.keys(selectedDocuments);
  const selectedSectionIdxs = Object.values(selectedDocuments).flatMap(
    (doc) => doc.selectedSectionIdx || [],
  );

  // Local state for company selection
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const handleCompanySelect = async (company: Company | null) => {
    setSelectedCompany(company);
    if (company) {
      try {
        dispatch(setCompanyAsync(company.id));
      } catch (error) {
        console.error('Error fetching company:', error);
      }
    } else {
      // Reset state when ticker is empty
      dispatch(reset());
    }
  };

  const showCustomFlow = summaryType === 'custom' && selectedCompany;
  const showPredefinedFlow = summaryType === 'predefined' && selectedCompany;

  const canGenerate =
    companyId &&
    ((summaryType === 'custom' && selectedSectionIdxs.length > 0 && prompt.trim()) ||
      (summaryType === 'predefined' && promptName));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Heading level={1}>Summary Workflow Testing</Heading>
        <Text>
          Test and configure summary generation workflows by selecting companies, documents,
          sections, and custom prompts.
        </Text>
      </div>

      <div className={styles.content}>
        <div className={styles.leftPanel}>
          <div className={styles.section}>
            <Heading level={2}>1. Select Company</Heading>
            <TickerInput
              value={selectedCompany}
              onChange={handleCompanySelect}
              placeholder="Enter company ticker..."
            />
          </div>

          {selectedCompany && (
            <div className={styles.section}>
              <SummaryTypeSelector />
            </div>
          )}

          {showPredefinedFlow && (
            <div className={styles.section}>
              <PromptNameSelector />
            </div>
          )}

          {showCustomFlow && (
            <>
              <div className={styles.section}>
                <Heading level={2}>3. Select Documents</Heading>
                <DocumentList />
              </div>

              {selectedDocumentIds.length > 0 && (
                <div className={styles.section}>
                  <Heading level={2}>4. Select Sections</Heading>
                  <SectionSelector />
                </div>
              )}

              {selectedSectionIdxs.length > 0 && (
                <div className={styles.section}>
                  <Heading level={2}>5. Configure Summary</Heading>
                  <ContextWindowStats />
                  <PromptInput />
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.rightPanel}>
          {canGenerate && (
            <div className={styles.section}>
              <Heading level={2}>Generate Summary</Heading>
              <SummaryResult />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryTestPage;
